import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Divider,
  CircularProgress, IconButton, Tooltip
} from '@mui/material';
import {
  Business, Inventory2, ShoppingCart, Description, Payment,
  ListAlt, SwapHoriz, EditNote, LocationOn, Home, Refresh,
  PendingActions, Warehouse, TrendingDown,
  MoveToInbox, Receipt, Assessment, HourglassBottom, PriceCheck,
  ReportProblem, AccountBalanceWallet
} from '@mui/icons-material';
import axios from 'axios';
import { fmtN, fmt, sym } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;

const SupplyChainDashboard = () => {
  const navigate = useNavigate();
  const [kpis,    setKpis]    = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchKpis = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      axios.get(`${API}/api/payables/orders`,       { headers }),  // 0
      axios.get(`${API}/api/payables/invoices`,     { headers }),  // 1
      axios.get(`${API}/api/payables/vouchers`,     { headers }),  // 2
      axios.get(`${API}/api/payables/requisitions`, { headers }),  // 3
      axios.get(`${API}/api/procurement/transfers`, { headers }),  // 4
      axios.get(`${API}/api/payables/items`,        { headers }),  // 5
      axios.get(`${API}/api/payables/locations`,    { headers }),  // 6
      axios.get(`${API}/api/payables/suppliers`,    { headers }),  // 7
      axios.get(`${API}/api/payables/item-journals`,{ headers }),  // 8
      axios.get(`${API}/api/payables/receipts`,     { headers }),  // 9
      axios.get(`${API}/api/payables/item-stock`,   { headers }),  // 10
    ]);
    const get = (i) => results[i].status === 'fulfilled' ? results[i].value.data : [];

    const orders   = get(0);
    const pinvs    = get(1);
    const pvs      = get(2);
    const reqs     = get(3);
    const xfers    = get(4);
    const receipts = get(9);
    const stock    = get(10) || {};

    // Not fully processed = still needs receiving/invoicing
    const liveOrder = (o) => !['Invoiced', 'Posted', 'Cancelled'].includes(o.status);
    // Received but not yet fully invoiced — awaiting the vendor invoice
    const toInvoice = (o) => ['Received', 'Partially Received', 'Partially Invoiced'].includes(o.status);

    const stockVals   = Object.values(stock);
    const stockValue  = stockVals.reduce((s, v) => s + (v.value || 0), 0);
    const negativeQty = stockVals.filter(v => (v.qty || 0) < 0).length;

    // Items at/below their reorder point (reorderPoint > 0, on-hand <= it)
    const itemsArr    = get(5);
    const belowReorder = itemsArr.filter(i =>
      i.itemType === 'Item' && i.isActive !== false && (i.reorderPoint || 0) > 0 &&
      (stock[i.itemNo]?.qty || 0) <= i.reorderPoint
    ).length;

    setKpis({
      openOrders:    orders.filter(liveOrder).length,
      totalOrders:   orders.length,
      toInvoice:     orders.filter(toInvoice).length,
      openPinv:      pinvs.filter(i => i.status !== 'Paid' && i.status !== 'Cancelled').length,
      totalPinv:     pinvs.length,
      pendingPv:     pvs.filter(v => v.status === 'Open' || v.status === 'Draft').length,
      totalPv:       pvs.length,
      openReqs:      reqs.filter(r => r.status === 'Open' || r.status === 'Draft').length,
      totalReqs:     reqs.length,
      openXfers:     xfers.filter(t => t.status !== 'Posted' && t.status !== 'Cancelled').length,
      totalXfers:    xfers.length,
      items:         get(5).length,
      locations:     get(6).length,
      suppliers:     get(7).length,
      journals:      get(8).length,
      receipts:      receipts.length,
      stockValue,
      negativeQty,
      belowReorder,
    });
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchKpis(); }, [fetchKpis]);

  const k = kpis || {};

  // ── KPI Cue ──────────────────────────────────────────────────────────────
  const Cue = ({ icon: Icon, label, value, sub, color, urgent, path }) => (
    <Card elevation={2} sx={{
      borderRadius: 3, cursor: path ? 'pointer' : 'default', height: '100%',
      borderLeft: `4px solid ${urgent ? '#c62828' : color}`,
      transition: 'transform .15s, box-shadow .15s',
      '&:hover': path ? { boxShadow: 5, transform: 'translateY(-2px)' } : {}
    }} onClick={() => path && navigate(path)}>
      <CardContent sx={{ py: 1.8 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="h4" fontWeight={800} lineHeight={1}
              color={urgent ? 'error.main' : color}>
              {loading ? '…' : (typeof value === 'string' ? value : fmtN(value))}
            </Typography>
            <Typography variant="body2" fontWeight={600} mt={0.5}>{label}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{
            width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${urgent ? '#c62828' : color}18`
          }}>
            <Icon sx={{ fontSize: 21, color: urgent ? '#c62828' : color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // ── Nav Tile ──────────────────────────────────────────────────────────────
  const Tile = ({ icon: Icon, label, desc, color, path, count }) => (
    <Card elevation={1} sx={{
      borderRadius: 2, cursor: 'pointer', height: '100%',
      transition: 'transform .15s, box-shadow .15s',
      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
    }} onClick={() => navigate(path)}>
      <CardContent sx={{ py: 1.5, px: 2 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{
            width: 38, height: 38, flexShrink: 0, borderRadius: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${color}18`
          }}>
            <Icon sx={{ fontSize: 20, color }} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={0.8}>
              <Typography variant="body2" fontWeight={700} lineHeight={1.2} noWrap>{label}</Typography>
              {count !== undefined && !loading && (
                <Typography variant="caption" sx={{
                  bgcolor: `${color}15`, color, fontWeight: 700,
                  px: 0.7, py: 0.1, borderRadius: 1, fontSize: 11
                }}>{fmtN(count)}</Typography>
              )}
            </Box>
            {desc && <Typography variant="caption" color="text.secondary" lineHeight={1.2}>{desc}</Typography>}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const Section = ({ title, color, tiles }) => (
    <Box mb={3.5}>
      <Box display="flex" alignItems="center" mb={1.5}>
        <Box sx={{ width: 4, height: 20, bgcolor: color, borderRadius: 2, mr: 1.5, flexShrink: 0 }} />
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        <Divider sx={{ flex: 1, ml: 1.5 }} />
      </Box>
      <Grid container spacing={1.5}>
        {tiles.map(t => <Grid item xs={12} sm={6} md={3} key={t.label}><Tile {...t} /></Grid>)}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {/* Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 3,
          background: 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)',
          color: '#fff', boxShadow: 3, flexWrap: 'wrap', gap: 2
        }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Supply Chain Manager</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3 }}>
              Purchasing · Stores & Inventory · Logistics
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchKpis} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Refresh />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Home">
              <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Activities */}
        <Box mb={1}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>Activities</Typography>
            <Divider sx={{ flex: 1, ml: 1.5 }} />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <Cue icon={ShoppingCart} label="Open Purchase Orders" color="#2e7d32"
                value={k.openOrders} sub={`of ${fmtN(k.totalOrders)} total`}
                path="/payables/orders" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Cue icon={MoveToInbox} label="Awaiting Invoice" color="#00838f"
                value={k.toInvoice} sub="received, not invoiced"
                path="/payables/orders" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Cue icon={PendingActions} label="Unpaid Invoices" color="#6a1b9a"
                value={k.openPinv} sub={`of ${fmtN(k.totalPinv)} total`}
                path="/payables/invoices" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Cue icon={PriceCheck} label="Stock Value" color="#1565c0"
                value={loading ? '…' : `${sym()} ${fmt(k.stockValue)}`}
                sub="on-hand inventory"
                path="/payables/inventory/valuation" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Cue icon={ReportProblem} label="Below Reorder" color="#f57c00"
                urgent={k.belowReorder > 0}
                value={k.belowReorder} sub="items to replenish"
                path="/payables/inventory/reorder" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Cue icon={TrendingDown} label="Negative Stock" color="#c62828"
                urgent={k.negativeQty > 0}
                value={k.negativeQty} sub="below zero — check"
                path="/payables/inventory/valuation" />
            </Grid>
          </Grid>
        </Box>

        <Box my={3}><Divider /></Box>

        {/* Vendors & Purchasing */}
        <Section title="Vendors & Purchasing" color="#e65100" tiles={[
          { icon: Business,     label: 'Vendors / Suppliers',  desc: 'Supplier master records',         color: '#e65100', path: '/suppliers',           count: k.suppliers   },
          { icon: ShoppingCart, label: 'Purchase Orders',       desc: 'Release · receive · invoice',     color: '#2e7d32', path: '/payables/orders',     count: k.totalOrders },
          { icon: MoveToInbox,  label: 'Goods Receipts',        desc: 'Posted receipts against POs',     color: '#00838f', path: '/payables/receipts',   count: k.receipts   },
          { icon: Description,  label: 'Purchase Invoices',     desc: 'Vendor invoices received',        color: '#6a1b9a', path: '/payables/invoices',   count: k.totalPinv   },
          { icon: Payment,      label: 'Payment Vouchers',      desc: 'Payments to vendors',             color: '#00695c', path: '/payables/vouchers',   count: k.totalPv     },
        ]} />

        {/* Stores & Inventory */}
        <Section title="Stores & Inventory" color="#1565c0" tiles={[
          { icon: Inventory2,  label: 'Items & Services',       desc: 'Item master list',                color: '#1565c0', path: '/payables/items',          count: k.items     },
          { icon: LocationOn,  label: 'Locations / Stores',     desc: 'Warehouses and store locations',  color: '#00695c', path: '/setups/locations',        count: k.locations },
          { icon: EditNote,    label: 'Item Journals',          desc: 'Stock adjustments & postings',    color: '#4e342e', path: '/payables/item-journals',  count: k.journals  },
          { icon: ListAlt,     label: 'Store Requisitions',     desc: 'Departmental stock requests',     color: '#4527a0', path: '/payables/requisitions',   count: k.totalReqs },
          { icon: SwapHoriz,   label: 'Transfer Orders',        desc: 'Stock between stores',            color: '#00796b', path: '/procurement/transfers',   count: k.totalXfers },
        ]} />

        {/* Inventory Reports & Analytics */}
        <Section title="Inventory Reports & Analytics" color="#6a1b9a" tiles={[
          { icon: PriceCheck,      label: 'Stock Valuation',       desc: 'Qty, avg cost & total value',     color: '#1565c0', path: '/payables/inventory/valuation' },
          { icon: Warehouse,       label: 'Availability by Location', desc: 'Where stock sits, per store',  color: '#00695c', path: '/payables/inventory/valuation' },
          { icon: ReportProblem,   label: 'Reorder / Replenish',   desc: 'Items at/below reorder point',    color: '#f57c00', path: '/payables/inventory/reorder'   },
          { icon: HourglassBottom, label: 'Inventory Aging',       desc: 'Slow-moving / old stock (FIFO)',  color: '#6a1b9a', path: '/payables/inventory/aging'     },
          { icon: AccountBalanceWallet, label: 'Payables Aging',   desc: 'What you owe vendors, by age',     color: '#c62828', path: '/payables/aging'              },
          { icon: Receipt,         label: 'VAT Return',            desc: 'Output − input VAT for a period', color: '#e65100', path: '/payables/vat-return'          },
          { icon: Assessment,      label: 'Item Ledger',           desc: 'Full movement history per item',  color: '#455a64', path: '/payables/items'               },
        ]} />

      </Box>
    </Box>
  );
};

export default SupplyChainDashboard;
